
import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OnboardingFormValues } from "../OnboardingFormSchema";
import { useWizard } from "../FormWizardContext";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";

const CURRENCY_OPTIONS = [
  { value: "USDT", label: "USDT" },
  { value: "USDC", label: "USDC" },
  { value: "MNT", label: "MNT" },
];

// Updated language options with code and description, including the new languages requested
const LANGUAGE_OPTIONS = [
  { value: "EN", label: "EN", description: "English" },
  { value: "KR", label: "KR", description: "Korean" },
  { value: "CN", label: "CN", description: "Chinese" },
  { value: "JP", label: "JP", description: "Japanese" },
  { value: "ES", label: "ES", description: "Spanish" },
  { value: "TR", label: "TR", description: "Turkish" },
  { value: "VN", label: "VN", description: "Vietnamese" },
  { value: "RU", label: "RU", description: "Russian" },
  { value: "ID", label: "ID", description: "Indonesian" },
  { value: "IN", label: "IN", description: "Hindi" },
  { value: "BR", label: "BR", description: "Brazil" },
  { value: "DE", label: "DE", description: "Germany" },
  { value: "IT", label: "IT", description: "Italy" },
  { value: "PH", label: "PH", description: "Philippines" },
  { value: "TH", label: "TH", description: "Thailand" },
  { value: "PT", label: "PT", description: "Portugal" },
  { value: "MY", label: "MY", description: "Malaysia" },
  { value: "AR", label: "AR", description: "Arab" },
  { value: "CANT", label: "CANT", description: "Cantonese" },
];

interface PicOption {
  id: string;
  name: string;
  email: string | null;
  position: string | null;
}

export const SocialMediaStep = ({ form }: { form: UseFormReturn<OnboardingFormValues> }) => {
  const { currentStep } = useWizard();
  const [picOptions, setPicOptions] = useState<PicOption[]>([]);
  const [loading, setLoading] = useState(false);
  const experienceRate = form.watch("experience_rate") || 0;

  useEffect(() => {
    const fetchPics = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('kol_pic')
          .select('id, name, email, position')
          .eq('position', 'regional_manager')
          .order('name');
          
        if (error) throw error;
        
        if (data) {
          setPicOptions(data);
        }
      } catch (error) {
        console.error('Error fetching PIC options:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPics();
  }, []);

  if (currentStep !== 2) return null;

  const handleExperienceRateChange = (value: string) => {
    form.setValue("experience_rate", parseInt(value));
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="agreed_rate"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 dark:text-gray-200">Compensation Amount</FormLabel>
              <FormControl>
                <Input 
                  placeholder="0" 
                  type="number"
                  step="0.01"
                  {...field} 
                  onChange={e => field.onChange(parseFloat(e.target.value) || 0)}
                  className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="currency_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 dark:text-gray-200">Token Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                    <SelectValue placeholder="Select token" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CURRENCY_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="region"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-gray-700 dark:text-gray-200">Region</FormLabel>
            <FormControl>
              <RadioGroup 
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex flex-wrap gap-2"
              >
                {LANGUAGE_OPTIONS.map((option) => (
                  <div key={option.value} className="flex items-center">
                    <RadioGroupItem 
                      value={option.value} 
                      id={`lang-${option.value}`}
                      className="peer sr-only"
                    />
                    <label
                      htmlFor={`lang-${option.value}`}
                      className="flex flex-col rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-900 peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50 peer-data-[state=checked]:text-blue-600 cursor-pointer"
                    >
                      <span>{option.value}</span>
                      <span className="text-xs text-gray-500">({option.description})</span>
                    </label>
                  </div>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Changed from badge selection to text input */}
      <FormField
        control={form.control}
        name="pic_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-gray-700 dark:text-gray-200">
              What is the name of your Mantle contact person for this collaboration?
            </FormLabel>
            <FormControl>
              <Input 
                placeholder="Enter contact person's name" 
                {...field}
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="experience_rate"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-gray-700 dark:text-gray-200">
              How was your experience in communicating with the contact person?
            </FormLabel>
            <div className="space-y-3">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(rating => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => handleExperienceRateChange(rating.toString())}
                    className="focus:outline-none"
                  >
                    <img 
                      src={rating <= experienceRate 
                        ? "/lovable-uploads/84ada803-7dc1-4ef8-beb4-47ee6e6e6213.png" 
                        : "/lovable-uploads/9a6b10d0-2ed6-45cc-bc0c-e0d4744b4bc1.png"}
                      alt={rating <= experienceRate ? "Rated icon" : "Unrated icon"}
                      className="w-10 h-10" 
                    />
                  </button>
                ))}
              </div>
              {experienceRate > 0 && (
                <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                  Rating: {experienceRate} out of 5
                </Badge>
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
